import { DateModeEnum, DatePicker, FormInputSearch, FormSelect, FormSelectDataType, getCountryData, getDate, toast, Tooltip } from '@systemeio/ui';
import ContactInfoTooltip from 'modules/contacts/components/contact-info-tooltip';
import { usePricePlanFilter } from 'modules/payment/hooks/use-price-plan-filter';
import { sendCreditNote, sendSalesInvoice } from 'modules/payment/transactions/api/transactions-api';
import ExportTransactions from 'modules/payment/transactions/components/export-transactions';
import RefundConfirmation from 'modules/payment/transactions/components/refund-confirmation-modal';
import TransactionAmountTooltip from 'modules/payment/transactions/components/transaction-amount-tooltip';
import TransactionPaymentTooltip from 'modules/payment/transactions/components/transaction-payment-tooltip';
import { TransactionsLoco } from 'modules/payment/transactions/constants/transactions-loco';
import { TransactionTypeEnum } from 'modules/payment/transactions/enums/transaction-type-enum';
import { useTransactions } from 'modules/payment/transactions/hooks/use-transactions';
import { TransactionFilterInterface, TransactionTypeInterface } from 'modules/payment/transactions/types/transaction-filter-interface';
import { TransactionInterface } from 'modules/payment/transactions/types/transaction-interface';
import { getOfferPricePlansData } from 'modules/payment/transactions/utils/get-offer-price-plan-data-utils';
import { isNewCustomer } from 'modules/payment/transactions/utils/isNewCustomer';
import { NextPage } from 'next';
import React, { useEffect, useState } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useListingSelect } from 'shared/hooks/use-listing-select';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import useWindowSize from 'shared/hooks/use-window-size';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { getToday } from 'shared/utils/get-dates';
import { CustomerTypeEnum } from 'modules/profile/user-settings/enums/customer-type-enum';
const defaultFilters: TransactionFilterInterface = {
  customerEmail: '',
  paymentProcessorChargeId: '',
  type: undefined,
  pricePlans: undefined,
  dateFrom: null,
  dateTo: null,
  orderItem: null,
  customerCountry: undefined,
  customerType: undefined
};
const Transaction: NextPage = () => {
  const {
    user,
    intlLocale
  } = useUser();
  const {
    setPagination,
    pagination,
    setLimit,
    limit,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters);
  const {
    t
  } = useLocoTranslation();
  const [offerPricePlanData, setOfferPricePlanData] = useState<FormSelectDataType<string>[]>();
  const typeData = (): TransactionTypeInterface[] => Object.keys(TransactionTypeEnum).map(key => {
    const type = key as keyof typeof TransactionTypeEnum;
    return {
      id: type,
      caption: t(TransactionTypeEnum[type])
    };
  });
  const {
    transactions,
    mutate,
    counter,
    mutateCounter
  } = useTransactions({
    limit,
    pagination,
    filter
  });
  const {
    pricePlanFilter
  } = usePricePlanFilter();
  const {
    renderSelectButton,
    getListingSelect,
    renderSelectAllButton
  } = useListingSelect({
    listing: transactions,
    count: counter?.count,
    filter: filter
  });
  useEffect(() => {
    if (pricePlanFilter) {
      setOfferPricePlanData(getOfferPricePlansData(pricePlanFilter, t));
    }
  }, [t, pricePlanFilter]);
  const onSendToCustomer = async (transaction: TransactionInterface) => {
    try {
      transaction.transactionType === 'sale' ? await sendSalesInvoice(transaction.payment.id) : await sendCreditNote(transaction.payment.id);
      toast.success(t('dashboard.transactions.email_send'));
    } catch (e) {
      toast.error(t('global.error'));
    }
  };
  const onUpdate = async () => {
    await mutate();
    await mutateCounter();
  };
  const columns: ColumnDefinitionInterface<TransactionInterface>[] = [{
    key: 'transaction_type',
    header: <div className={`flex ${transactions && 'gap-3'}`}>
          <div className="hidden items-center lg:flex">
            {transactions && transactions.items.length !== 0 && renderSelectAllButton()}
          </div>
          <Tooltip mode={'overflow'} label={t('dashboard.transactions.transaction_date')} wrapperClassName={'w-full'}>
            <span className="truncate">{t('dashboard.transactions.transaction_date')}</span>
          </Tooltip>
        </div>,
    onRender: transaction_0 => <div className="flex gap-3">
          <div className="flex items-center">{renderSelectButton(transaction_0)}</div>
          <div className="flex flex-col">
            <span className="text-text-primary">
              {`${transaction_0.refund ? t('dashboard.transactions.receipt') : t('dashboard.transactions.sales_invoice')} #${transaction_0.invoice.invoiceNum}`}
            </span>
            <span className="text-text-primary/50">
              {dateTimeFormat(transaction_0.invoice.payment.paidAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone
          })}
            </span>
          </div>
        </div>,
    widthPercent: 30
  }, {
    key: 'invoice.customer',
    header: t('global.customer'),
    onRender: transaction_1 => <div className={`flex w-full items-center gap-2`}>
          <ContactInfoTooltip wrapperClassName={'w-fit shrink-0'} fields={transaction_1.invoice.customer.fieldValues} referredByEmail={transaction_1.invoice.customer.referredBy?.email} type={transaction_1.invoice.customer.type}>
            <img className={'h-[48px] w-[48px] rounded-full'} src={transaction_1.avatar} alt={'avatar'} />
          </ContactInfoTooltip>
          <div className="flex w-full flex-col justify-center gap-2 overflow-hidden">
            <div className={'flex items-center gap-2'}>
              <span className="text-text-primary truncate">
                {transaction_1.invoice.customer.fullName}
              </span>
              {isNewCustomer(transaction_1.invoice.customer.contact.firstPaymentAt) && <Tooltip label={<div className={'flex flex-col gap-2.5'}>
                      <span className={'self-center font-bold'}>
                        {t('dashboard.transactions.customer.new.tooltip.title')}
                      </span>
                      <span>{t('dashboard.transactions.customer.new.tooltip.description')}</span>
                    </div>} mode={'hover'}>
                  <span className="bg-green text-background-secondary rounded-md px-2 py-1 text-xs">
                    {t('dashboard.transactions.customer.new.tag')}
                  </span>
                </Tooltip>}
            </div>
            <div className="truncate">
              {transaction_1.invoice.customer.contact.removedAt !== null ? transaction_1.invoice.customer.email : <LinkWithoutPrefetch className="primary-link w-full" href={`/contacts/${transaction_1.invoice.customer.contact.id}`}>
                  {transaction_1.invoice.customer.email}
                </LinkWithoutPrefetch>}
            </div>
          </div>
        </div>,
    widthPercent: 30
  }, {
    key: 'amount',
    header: t('dashboard.transactions.amount'),
    widthPercent: 20,
    onRender: transaction_2 => <TransactionAmountTooltip transaction={transaction_2} />
  }, {
    key: 'name',
    header: t('dashboard.transactions.offer'),
    onRender: transaction_3 => <div className={'flex flex-col'}>
          {transaction_3.payment.paymentItems.map((el, idx) => <div key={el.id} className={`flex flex-col gap-1 py-3 ${idx !== 0 && 'border-text-secondary/30 border-t'} ${idx === 0 && 'pt-0'} ${idx + 1 === transaction_3.payment.paymentItems.length && 'pb-0'}`}>
              <TransactionPaymentTooltip paymentItem={el} />
            </div>)}
        </div>,
    widthPercent: 20
  }];
  const {
    width
  } = useWindowSize();
  return <>
      <ListingLayout counter={counter} data={transactions} title={t('dashboard.transactions.transactions_title')} type={'side'} filterData={{
      filterer: {
        filter,
        setFilter,
        defaultFilters
      },
      paginator: {
        pagination,
        setPagination
      },
      limiter: {
        limit,
        setLimit
      },
      testAttributePostfix: 'filter-btn',
      filterable: [{
        field: 'customerEmail',
        onRender: (value, onChange) => <FormInputSearch key={'transaction-email-filter'} placeholder={t('dashboard.transactions.customer_email')} onClear={() => onChange('')} value={value} onChange={e_0 => onChange(e_0.target.value)} className="[&>input]:truncate [&>input]:pr-10 [&>input]:placeholder-shown:truncate lg:min-w-0" testAttributePostfix={'filter-email'} />
      }, {
        field: 'customerCountry',
        onRender: (value_0, onChange_0) => <FormSelect key={'transaction-country-filter'} placeholder={t('dashboard.user.country')} value={value_0} onChange={onChange_0} data={getCountryData(user?.dashboardLocale)} className="truncate placeholder-shown:truncate lg:min-w-0" />
      }, {
        field: 'customerType',
        onRender: (value_1, onChange_1) => <FormSelect key={'transaction-customer-type-filter'} placeholder={t('dashboard.user.customer_type')} value={value_1} onChange={onChange_1} data={[{
          id: 'personal',
          caption: t(CustomerTypeEnum.personal)
        }, {
          id: 'company',
          caption: t(CustomerTypeEnum.company)
        }]} className="truncate placeholder-shown:truncate lg:min-w-0" />
      }, {
        field: 'paymentProcessorChargeId',
        onRender: (value_2, onChange_2) => <FormInputSearch key={'transaction-payment-processor-charge-id-filter'} placeholder={t('dashboard.transactions.payment_processor_charge_id')} onClear={() => onChange_2('')} value={value_2} onChange={e_1 => onChange_2(e_1.target.value)} className="[&>input]:truncate [&>input]:pr-10 [&>input]:placeholder-shown:truncate lg:min-w-0" testAttributePostfix={'filter-payment-processor-charge-id'} />
      }, {
        field: 'pricePlans',
        onRender: (value_3, onChange_3) => <FormSelect key={'transaction-offer-filter'} data={offerPricePlanData} onChange={data => onChange_3(data?.split(',').map(el_0 => Number(el_0)))} value={value_3?.join(',')} placeholder={t('dashboard.transactions.select_price_plan')} className="truncate placeholder-shown:truncate lg:min-w-0" />
      }, {
        field: 'type',
        onRender: (value_4, onChange_4) => <FormSelect key={'transaction-type-filter'} data={typeData()} onChange={data_0 => onChange_4(data_0)} value={value_4} placeholder={t('dashboard.transactions.any_type')} />
      }, {
        field: 'dateFrom',
        onRender: (value_5, onChange_5) => <DatePicker key={'transaction-dateSince-filter'} maxDate={getToday(user?.timezone)} minDate={getDate(user?.createdAt, user?.timezone)} value={value_5} onChange={date => onChange_5(date)} placeholder={t('dashboard.transactions.date_since')} wrapperButtonInputClassName="truncate" timezone={user?.timezone} createdAt={user?.createdAt} intlLocale={intlLocale} />
      }, {
        field: 'dateTo',
        onRender: (value_6, onChange_6) => <DatePicker mode={DateModeEnum.DateTill} maxDate={getToday(user?.timezone)} minDate={getDate(user?.createdAt, user?.timezone)} key={'transaction-dateTill-filter'} value={value_6} onChange={date_0 => onChange_6(date_0)} placeholder={t('dashboard.transactions.date_till')} wrapperButtonInputClassName="truncate" timezone={user?.timezone} createdAt={user?.createdAt} intlLocale={intlLocale} />
      }]
    }}>
        <div className={'mb-28 w-full md:mb-20'}>
          {transactions && transactions.items.length !== 0 && <div className={'bg-brand-primary-subtle flex items-center gap-3 rounded-t-lg px-6 py-4 lg:hidden'}>
              {renderSelectAllButton()}
              <span>{t('global.select_all')}</span>
            </div>}
          <Table type={'small'} firstLineRect={width < 1024 ? !!transactions : false} data={transactions} testAttributePostfix="transaction" columns={columns} limiter={{
          limit,
          setLimit
        }} paginator={{
          pagination,
          setPagination
        }} actions={{
          custom: [{
            shouldRender: transaction_4 => !transaction_4.refund && transaction_4.payment.canBeRefunded,
            name: 'refund',
            caption: t('dashboard.transactions.refund'),
            onActionRender: (opened, onClose, transaction_5, key_0) => <RefundConfirmation key={key_0} opened={opened} onClose={onClose} transactionId={transaction_5.payment.id} paymentProcessorName={transaction_5.payment.paymentProcessorName} onUpdate={onUpdate} />
          }, {
            name: 'send-invoice',
            caption: t('dashboard.transactions.send_invoice'),
            onActionClick: transaction_6 => onSendToCustomer(transaction_6)
          }],
          openLink: [{
            openInNew: true,
            name: 'download-invoice',
            setHref: transaction_7 => transaction_7.transactionType === 'sale' ? `/payment/sales-invoice/${transaction_7.invoice.id}/download` : `/payment/credit-note/${transaction_7.payment.id}/download`
          }]
        }} />
        </div>
      </ListingLayout>
      <ExportTransactions filter={filter} getListingSelect={getListingSelect} />
    </>;
};
export const getServerSideProps = getBaseServerSideProps(TransactionsLoco, 'header.top_menu.transactions');
export default Transaction;